import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import axios from 'axios';
import { BASE_URL } from '../../AdminComponent/BaseUrl';
import logo from '../../assets/frontimg/logo_new_2.png';
import { getCartCount } from '../../Store/Cart/cart-action';
import LoginForm from '../Authentication/LoginForm';
import './MobileHeader.css';

const MobileHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedMenuItem, setExpandedMenuItem] = useState(null);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [menuData, setMenuData] = useState({
    groups: [],
    furniture: [],
    decor: [],
    tableware: []
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const cartCount = useSelector((state) => state.cartCount.cartCount);
  const custUserId = Cookies.get('custuserid');

  // Fetch menu data
  useEffect(() => {
    const fetchMenuData = async () => {
      try {
        const [groupRes, furnitureRes, decorRes, tablewareRes] = await Promise.all([
          axios.get(`${BASE_URL}/group_data`),
          axios.get(`${BASE_URL}/furniture_data`),
          axios.get(`${BASE_URL}/decor_data`),
          axios.get(`${BASE_URL}/tableware_data`)
        ]);

        setMenuData({
          groups: groupRes.data,
          furniture: furnitureRes.data,
          decor: decorRes.data,
          tableware: tablewareRes.data
        });
      } catch (error) {
        console.error('Error fetching menu data:', error);
      }
    };

    fetchMenuData();
    dispatch(getCartCount());
  }, [dispatch]);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setExpandedMenuItem(null);
  }, [location.pathname]);

  // Handle body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setExpandedMenuItem(null);
  };

  const toggleMenuItem = (menuItem) => {
    setExpandedMenuItem(expandedMenuItem === menuItem ? null : menuItem);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setExpandedMenuItem(null);
  };

  const handleLoginClick = () => {
    if (!custUserId) {
      setShowLoginForm(true);
      closeMenu();
    }
  };

  const menuItems = [
    {
      id: 'home',
      title: 'Home',
      link: '/home',
      hasSubmenu: false
    },
    {
      id: 'furniture',
      title: 'Furniture',
      link: `/shoproduct/${menuData.groups[0]?.slug}`,
      hasSubmenu: true,
      submenu: menuData.furniture
    },
    {
      id: 'decor',
      title: 'Decor',
      link: `/shoproduct/${menuData.groups[1]?.slug}`,
      hasSubmenu: true,
      submenu: menuData.decor
    },
    {
      id: 'tableware',
      title: 'Tableware',
      link: `/shoproduct/${menuData.groups[2]?.slug}`,
      hasSubmenu: true,
      submenu: menuData.tableware
    },
    {
      id: 'luxe',
      title: 'Luxe',
      link: '/luxe',
      hasSubmenu: false
    },
    {
      id: 'customization',
      title: 'Customization',
      link: '/customizationpage',
      hasSubmenu: false
    },
    {
      id: 'contact',
      title: 'Contact Us',
      link: '/contact',
      hasSubmenu: false
    }
  ];

  return (
    <>
      {/* Mobile Header */}
      <header className="mobile-header-container">
        <div className="mobile-header-content">
          {/* Left: Hamburger Menu */}
          <button 
            className="mobile-hamburger-btn"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <div className={`hamburger-icon ${isMenuOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>

          {/* Center: Logo */}
          <div className="mobile-logo-container">
            <Link to="/home" onClick={closeMenu}>
              <img 
                src={logo} 
                alt="" 
                className="mobile-logo-img"
              />
            </Link>
          </div>

          {/* Right: Login & Cart */}
          <div className="mobile-header-actions">
            {/* Login */}
            <div className="mobile-login-container">
              {!custUserId ? (
                <button 
                  className="mobile-login-btn"
                  onClick={handleLoginClick}
                >
                  Login
                </button>
              ) : (
                <Link 
                  to="/profile" 
                  className="mobile-login-btn"
                  onClick={closeMenu}
                >
                  Profile
                </Link>
              )}
            </div>

            {/* Cart */}
            <Link 
              to="/shopcart" 
              className="mobile-cart-btn"
              onClick={closeMenu}
            >
              <div className="mobile-cart-icon">
                <i className="icon-large-paper-bag"></i>
                <span className="mobile-cart-count">{cartCount || 0}</span>
              </div>
            </Link>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={`mobile-menu-overlay ${isMenuOpen ? 'open' : ''}`}
        onClick={closeMenu}
      />

      {/* Mobile Menu Sidebar */}
      <div className={`mobile-menu-sidebar ${isMenuOpen ? 'open' : ''}`}>
        {/* Menu Header */}
        <div className="mobile-menu-header">
          <div className="mobile-menu-logo">
            <img src={logo} alt="" />
          </div>
          <button 
            className="mobile-menu-close"
            onClick={closeMenu}
            aria-label="Close menu"
          >
            <i className="fa fa-times"></i>
          </button>
        </div>

        {/* Menu Items */}
        <nav className="mobile-menu-nav">
          <ul className="mobile-menu-list">
            {menuItems.map((item) => (
              <li key={item.id} className="mobile-menu-item">
                <div className="mobile-menu-item-header">
                  <Link 
                    to={item.link}
                    className="mobile-menu-link"
                    onClick={!item.hasSubmenu ? closeMenu : undefined}
                  >
                    {item.title}
                  </Link>
                  {item.hasSubmenu && (
                    <button
                      className={`mobile-submenu-toggle ${
                        expandedMenuItem === item.id ? 'expanded' : ''
                      }`}
                      onClick={() => toggleMenuItem(item.id)}
                      aria-label={`Toggle ${item.title} submenu`}
                    >
                      <i className="fa fa-chevron-right"></i>
                    </button>
                  )}
                </div>
                
                {/* Submenu */}
                {item.hasSubmenu && (
                  <ul className={`mobile-submenu ${
                    expandedMenuItem === item.id ? 'expanded' : ''
                  }`}>
                    {item.submenu.map((subItem) => (
                      <li key={subItem.id} className="mobile-submenu-item">
                        <Link 
                          to={`/shoproduct/${subItem.slug}`}
                          className="mobile-submenu-link"
                          onClick={closeMenu}
                        >
                          {subItem.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Menu Footer */}
        <div className="mobile-menu-footer">
          <div className="mobile-menu-social">
            <a href="#" className="mobile-social-link">
              <i className="fa fa-facebook"></i>
            </a>
            <a href="#" className="mobile-social-link">
              <i className="fa fa-instagram"></i>
            </a>
            <a href="#" className="mobile-social-link">
              <i className="fa fa-linkedin"></i>
            </a>
            <a href="#" className="mobile-social-link">
              <i className="fa fa-pinterest"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Login Form Modal */}
      {showLoginForm && (
        <LoginForm 
          setOpen={setShowLoginForm} 
          open={showLoginForm} 
        />
      )}
    </>
  );
};

export default MobileHeader;
